// Global
import React from 'react';

// Local
import { ContentGrid, ContentGridCardAlignment } from '../ContentGrid/ContentGrid';
import { ContentQueryProvider } from '../ContentGrid/ContentQueryContext';
import { FacetConfig, FacetConfigProvider } from '../ContentGrid/FacetConfigContext';
import {
  CardFields,
  fieldTypes,
  ContentOverride,
  TimedReleaseOverride,
} from '../CardListingShared';
import { SearchResponse } from 'lib/services/models/search-response';
import { Themes } from 'lib/themes';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';
import { CtaFields, HeadlineFields } from 'lib/types/headlineFields';
import { Item } from '@sitecore-content-sdk/nextjs';
import { ContentGridContentResultAnalyticsConfig } from 'components/helpers/Analytics/ContentGrid/ContentGridAnalytics';

export type SearchType = 'Standard' | 'TypeAhead' | 'PreSearch';

export interface AdvancedContentGridProps extends Partial<HeadlineFields>, Partial<CtaFields> {
  initialSearchResponse?: SearchResponse | null;
  searchTerm?: string;
  searchType?: SearchType;
  searchAnalyticsEnabled?: boolean;
  children?: React.ReactNode;
  contentListItem?: Item;
  facetConfig?: FacetConfig;
  searchConfigurationId?: string;
  theme?: Themes;
  fieldsToShow?: CardFields[];
  columns?: '1' | '2' | '3' | '4';
  gridAspectRatio?: ImageAspectRatio;
  contentOverrides?: Record<string, ContentOverride>;
  timedReleaseOverrides?: Record<string, TimedReleaseOverride>;
  preambleOrientation?: PreambleOrientation;
  cardAlignment?: ContentGridCardAlignment;
  centerLastRow?: boolean;
  constrainedComponentWidth?: boolean;
  showPagination?: boolean;
  showResultCount?: boolean;
  showSorting?: boolean;
  showFacets?: boolean;
  showManageFavorites?: boolean;
  enableFavoritesAPI?: boolean;
  pageSize?: number;
  sortByOptions?: Item[];
  sortByDirections?: Item[];
  contentResultAnalytics?: ContentGridContentResultAnalyticsConfig;
}

export const AdvancedContentGrid = (props: AdvancedContentGridProps) => {
  const {
    initialSearchResponse = null,
    searchTerm,
    searchType,
    searchAnalyticsEnabled = false,
    contentListItem,
    facetConfig,
    searchConfigurationId,
    theme = 'ThemesPrimary',
    fieldsToShow = [...fieldTypes],
    columns = '3',
    contentOverrides = {},
    timedReleaseOverrides = {},
    showPagination = true,
    showResultCount = true,
    showSorting = true,
    showFacets = false,
    enableFavoritesAPI = false,
    ...rest
  } = props;

  return (
    <FacetConfigProvider facetConfig={facetConfig}>
      <ContentQueryProvider
        withFacets={showFacets}
        contentListItem={contentListItem}
        initialSearchResponse={initialSearchResponse}
        searchConfigurationId={searchConfigurationId}
        runtimeSearchTerm={searchTerm}
        enableFavoritesAPI={enableFavoritesAPI}
        pageSize={rest.pageSize}
      >
        {props.children}
        <ContentGrid
          theme={theme}
          contentOverrides={contentOverrides}
          timedReleaseOverrides={timedReleaseOverrides}
          fieldsToShow={fieldsToShow}
          columns={columns}
          gridAspectRatio={rest.gridAspectRatio}
          preambleOrientation={rest.preambleOrientation}
          cardAlignment={rest.cardAlignment}
          centerLastRow={rest.centerLastRow}
          constrainedComponentWidth={rest.constrainedComponentWidth}
          showPagination={showPagination}
          showResultCount={showResultCount}
          showSorting={showSorting}
          showFacets={showFacets}
          showManageFavorites={rest.showManageFavorites}
          sortByOptions={rest.sortByOptions}
          sortByDirections={rest.sortByDirections}
          searchType={searchType}
          searchAnalyticsEnabled={searchAnalyticsEnabled}
          contentResultAnalytics={rest.contentResultAnalytics}
          headline={rest.headline}
          headlineEyebrow={rest.headlineEyebrow}
          headlineSubhead={rest.headlineSubhead}
          headlineDescription={rest.headlineDescription}
          cta1={rest.cta1}
          cta2={rest.cta2}
        />
      </ContentQueryProvider>
    </FacetConfigProvider>
  );
};
